// PARKED from the onboarding flow (OnboardingPage now uses a lightweight finalize
// loader in place of the publication-search part), but still LIVE via
// `AuthorSearchPage` at `/discovery/author-search`. The interest step is
// specialty-scoped JuisciTopics (the closed vocabulary that replaced the old
// MeSH-term picker); see `TopicsView`.
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { IconCheck, IconLoader2 } from "@tabler/icons-react";
import { apiClient, type Api } from "../../services/api";
import { displayToast } from "../../components/app/AppToast";
import {
  gaEventMatchingPublicationsSubmitted,
  gaEventPubSearchError,
  gaEventPubSearchNoResults,
  gaEventRelevantTopicsSubmitted,
  type StepSkipTrigger,
} from "../../tools/analytics/domains/onboarding.analytics";
import { mixpanelSetUserProperties } from "../../tools/analytics/base.analytics";
import { Button } from "../../components/ui/Button";
import { FadeScroll } from "../../components/ui/FadeScroll";
import { ListItem } from "../../components/ui/ListItem";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { formatArticleDate } from "../../domains/app/utils/renderDate";
import PubMedLogo from "../../assets/icons/pubmed-logo.svg?react";
import { cn } from "../../lib/utils";

function yearFromIsoDate(d: string | null): string | null {
  return d === null ? null : d.split("-")[0];
}

type SearchResult =
  Api["post"]["/webapp/private/users/me/author-search"]["output"]["body"];
type Publication =
  SearchResult["candidates"][number]["topPublications"][number];
type Topic = Api["get"]["/webapp/private/topics"]["output"]["body"][number];

function pubKey(pub: Publication): string {
  if (pub.doi !== null) return pub.doi;
  if (pub.pmid !== null) return pub.pmid;
  return pub.title;
}

// "all" = every work the author has, minus `except` (opt-outs the user toggled
// off after selecting all). "some" = exactly `keys`. Keeping these as a union
// (rather than a flag + set) makes "all" independent of how many pages are
// currently loaded — the headline can read 285 selected before page 2 arrives.
type Selection =
  { mode: "all"; except: Set<string> } | { mode: "some"; keys: Set<string> };

function isPubSelected(selection: Selection, key: string): boolean {
  return selection.mode === "all"
    ? !selection.except.has(key)
    : selection.keys.has(key);
}

// Page size for browsing an author's works past the initial pool (infinite
// scroll). 100 = OpenAlex's practical max per page, so a prolific author's whole
// corpus is reached in few round-trips. Browsing starts at the server's
// `nextWorksOffset` (= pool size), so pages are contiguous with no overlap.
const WORKS_PAGE_SIZE = 100;

export function OnboardingPublicationSearch({
  firstName,
  lastName,
  specialtyTagId,
  searchPublications,
  accountReady,
  onPhaseChange,
  onComplete,
}: {
  firstName: string;
  lastName: string;
  // The selected specialty's Tag id. Scopes the interest step DURING onboarding,
  // before the specialty is persisted on the user row (persistence happens at
  // step completion). null → fall back to the user's persisted specialties.
  specialtyTagId: string | null;
  searchPublications: boolean;
  accountReady: Promise<void>;
  onPhaseChange: (phase: StepSkipTrigger) => void;
  onComplete: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const fullName = `${firstName} ${lastName}`;

  const [phase, setPhase] = useState<
    "pubmed-intro" | "searching" | "results" | "interests"
  >(searchPublications ? "pubmed-intro" : "searching");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [publications, setPublications] = useState<
    Array<Publication & { candidateId: string | null }>
  >([]);
  // Selection is either an explicit set of keys ("some") or "everything the
  // author has, minus a few opt-outs" ("all"). The "all" branch lets "Select
  // all" be instant and cover the whole corpus even before every page has
  // streamed in — the remaining pages load in the background and only need to be
  // resolved at confirm time. Modelled as a union so "all" can never silently
  // degrade to "only the rows currently loaded".
  const [selection, setSelection] = useState<Selection>({
    mode: "some",
    keys: new Set(),
  });
  const [searchSteps, setSearchSteps] = useState([
    { label: t("Connecting to PubMed"), done: false },
    { label: t("Searching by name"), done: false },
    { label: t("Fetching from PubMed"), done: false },
  ]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopicUids, setSelectedTopicUids] = useState<Set<string>>(
    new Set()
  );
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsSaving, setTopicsSaving] = useState(false);

  // The main search returns the "smart" ranked pool (in `publications`); the
  // rest of the author's works are browsed on demand via infinite scroll.
  // `morePublications` accumulates the browsed pages; `worksOffset` is the next
  // OpenAlex offset to fetch (null = nothing left / not an OpenAlex candidate).
  const [morePublications, setMorePublications] = useState<
    Array<Publication & { candidateId: string | null }>
  >([]);
  const [worksOffset, setWorksOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Synchronous re-entrancy guard: FadeScroll fires onReachBottom on every
  // scroll/resize tick near the bottom, faster than `loadingMore` state flushes.
  // Also shared with the background "select all" prefetch so scroll-triggered
  // loads can't race it.
  const loadingMoreRef = useRef(false);
  // The in-flight (or settled) background prefetch kicked off by "Select all".
  // It resolves to the FULL corpus so confirm reads a fresh array instead of a
  // stale `displayedPublications` closure. Null until "Select all" is used.
  const prefetchAllRef = useRef<Promise<
    Array<Publication & { candidateId: string | null }>
  > | null>(null);
  // Aborts every in-flight `/works` request (scroll pagination + the "select
  // all" prefetch) when the user leaves this step or restarts the search.
  // Created lazily so each search session gets a fresh controller. Only `/works`
  // carries this signal — the `/confirm` POST has none, so it always completes.
  const worksAbortRef = useRef<AbortController | null>(null);
  // Set only when the user moves FORWARD out of this step (saving interests).
  // Distinguishes completion (let the background finalize finish persisting)
  // from back-navigation/teardown (cancel the now-abandoned `/works`).
  const completingRef = useRef(false);

  function worksAbortSignal(): AbortSignal {
    if (worksAbortRef.current === null) {
      worksAbortRef.current = new AbortController();
    }
    return worksAbortRef.current.signal;
  }

  // Cancel and reset background `/works` traffic. Called on a new search and on
  // unmount. A subsequent `/works` lazily spins up a new controller.
  function abortWorks(): void {
    const controller = worksAbortRef.current;
    if (controller !== null) controller.abort();
    worksAbortRef.current = null;
    prefetchAllRef.current = null;
  }

  // Cancel background `/works` traffic when this step unmounts (back-navigation
  // to redo it, or any other teardown). Lifecycle teardown only — no mount-time
  // side effect or state sync. Inlined (not `abortWorks`) so the empty dep array
  // stays honest.
  useEffect(() => {
    return () => {
      // Completing forward: leave the background finalize running so it can
      // persist the full confirmed corpus. Any other teardown cancels `/works`.
      if (completingRef.current) return;
      const controller = worksAbortRef.current;
      if (controller !== null) controller.abort();
    };
  }, []);

  const firstCandidate =
    searchResult === null ? undefined : searchResult.candidates.at(0);
  const authorOpenalexId =
    firstCandidate === undefined ? null : firstCandidate.openalexId;
  // Total found for this author (PubMed-indexed). Shown as the headline up front;
  // the list itself streams in via infinite scroll and can be smaller.
  const totalFound =
    searchResult === null ? 0 : searchResult.totalPublicationsFound;

  // The full displayed list = pool (newest-first) then browsed pages appended
  // (also newest-first, continuing past the pool). Classic infinite scroll: no
  // global re-sort, items only ever append at the bottom. Deduped on insert.
  const displayedPublications = useMemo(
    () => [...publications, ...morePublications],
    [publications, morePublications]
  );

  // Size of the selectable universe: the real count once everything is loaded
  // (`worksOffset === null`), otherwise the author's headline total so the "all"
  // count reads right immediately while pages are still streaming in.
  const selectableCount =
    worksOffset === null ? displayedPublications.length : totalFound;
  const selectedCount =
    selection.mode === "all"
      ? Math.max(0, selectableCount - selection.except.size)
      : selection.keys.size;
  // "Unselect all" is only offered once truly everything is selected.
  const allSelected = selection.mode === "all" && selection.except.size === 0;

  async function loadMoreWorks(): Promise<void> {
    if (
      loadingMoreRef.current ||
      worksOffset === null ||
      authorOpenalexId === null
    )
      return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const response = await apiClient.post(
        "/webapp/private/users/me/author-search/works",
        {
          json: {
            openalexId: authorOpenalexId,
            limit: WORKS_PAGE_SIZE,
            offset: worksOffset,
          },
          signal: worksAbortSignal(),
        }
      );
      const { publications: pagePubs, hasMore } = response.body;
      setMorePublications((prev) => {
        const seen = new Set([...publications, ...prev].map((p) => pubKey(p)));
        const fresh = pagePubs
          .filter((p) => !seen.has(pubKey(p)))
          .map((p) => ({ ...p, candidateId: authorOpenalexId }));
        return [...prev, ...fresh];
      });
      setWorksOffset(hasMore ? worksOffset + WORKS_PAGE_SIZE : null);
    } catch {
      // Stop browsing on error — the user keeps the pool already shown.
      setWorksOffset(null);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  // biome-ignore lint: fire-and-forget on mount
  useState(() => {
    if (!searchPublications) {
      void loadInterests();
    }
  });

  // Surface the current skip-bucket to parent so the header Skip button can
  // tag matching_publications_skipped with "publications" vs "topics".
  useEffect(() => {
    onPhaseChange(phase === "interests" ? "topics" : "publications");
  }, [phase, onPhaseChange]);

  function handleStartSearch(): void {
    setPhase("searching");
    void runSearch();

    setTimeout(() => {
      setSearchSteps((s) =>
        s.map((st, i) => (i === 0 ? { ...st, done: true } : st))
      );
    }, 800);
    setTimeout(() => {
      setSearchSteps((s) =>
        s.map((st, i) => (i <= 1 ? { ...st, done: true } : st))
      );
    }, 1500);
  }

  async function runSearch(): Promise<void> {
    // A new search session abandons any prior one: stop its background `/works`
    // before starting over.
    abortWorks();
    try {
      // Wait for account creation to complete before calling authenticated endpoint
      await accountReady;

      const response = await apiClient.post(
        "/webapp/private/users/me/author-search",
        { json: { firstname: firstName, lastname: lastName } }
      );
      const data = response.body;

      setSearchSteps((s) => s.map((st) => ({ ...st, done: true })));
      setSearchResult(data);

      const pubs = data.candidates.flatMap((c) =>
        c.topPublications.map((pub) => ({
          ...pub,
          candidateId: c.openalexId,
        }))
      );
      setPublications(pubs);
      // Where infinite scroll continues browsing beyond the ranked pool. Null
      // for empty results / PubMed-only fallback (nothing to paginate).
      setWorksOffset(data.nextWorksOffset);

      setTimeout(() => {
        if (pubs.length > 0) {
          setPhase("results");
        } else {
          gaEventPubSearchNoResults();
          displayToast(
            t(
              "No publications found under this name.\nNo worries, you can still set up your feed manually."
            ),
            "info"
          );
          void loadInterests();
        }
      }, 500);
    } catch {
      gaEventPubSearchError();
      displayToast(
        t(
          "No publications found under this name.\nNo worries, you can still set up your feed manually."
        ),
        "info"
      );
      void loadInterests();
    }
  }

  function togglePublication(pub: Publication): void {
    const key = pubKey(pub);
    setSelection((prev) => {
      if (prev.mode === "all") {
        const except = new Set(prev.except);
        if (except.has(key)) {
          except.delete(key);
        } else {
          except.add(key);
        }
        return { mode: "all", except };
      }
      const keys = new Set(prev.keys);
      if (keys.has(key)) {
        keys.delete(key);
      } else {
        keys.add(key);
      }
      return { mode: "some", keys };
    });
  }

  function toggleAllPublications(): void {
    // Pure selection toggle — no network. The full corpus is only fetched once
    // the user actually confirms (in the background finalize), so nothing hits
    // `/works` just from selecting all.
    setSelection(
      allSelected
        ? { mode: "some", keys: new Set() }
        : { mode: "all", except: new Set() }
    );
  }

  // Paginate every remaining page of the author's works and append them to the
  // list. Returns the FULL corpus so confirm can read a fresh array (not a stale
  // `displayedPublications` closure). The result is memoised on `prefetchAllRef`
  // so a re-entrant call reuses the one in-flight fetch.
  function ensureAllWorksLoaded(): Promise<
    Array<Publication & { candidateId: string | null }>
  > {
    if (prefetchAllRef.current !== null) return prefetchAllRef.current;
    const promise = fetchAllRemainingWorks();
    prefetchAllRef.current = promise;
    return promise;
  }

  async function fetchAllRemainingWorks(): Promise<
    Array<Publication & { candidateId: string | null }>
  > {
    // Snapshot what's already loaded; pages are appended onto this.
    const corpus = [...publications, ...morePublications];
    if (worksOffset === null || authorOpenalexId === null) return corpus;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      // Derive each page's offset from a counter (never from the response), so
      // the API call's input doesn't depend on its own output's `hasMore`.
      const startOffset = worksOffset;
      let page = 0;
      let more = true;
      while (more) {
        const offset = startOffset + page * WORKS_PAGE_SIZE;
        const response = await apiClient.post(
          "/webapp/private/users/me/author-search/works",
          {
            json: {
              openalexId: authorOpenalexId,
              limit: WORKS_PAGE_SIZE,
              offset,
            },
            signal: worksAbortSignal(),
          }
        );
        const { publications: pagePubs, hasMore } = response.body;
        const seen = new Set(corpus.map((p) => pubKey(p)));
        const fresh = pagePubs
          .filter((p) => !seen.has(pubKey(p)))
          .map((p) => ({ ...p, candidateId: authorOpenalexId }));
        corpus.push(...fresh);
        setMorePublications((prev) => [...prev, ...fresh]);
        more = hasMore;
        page += 1;
        setWorksOffset(hasMore ? startOffset + page * WORKS_PAGE_SIZE : null);
      }
      return corpus;
    } catch {
      // Stop browsing on error — the user keeps the pool already shown. The
      // prefetch handle is cleared so a later toggle can retry.
      setWorksOffset(null);
      prefetchAllRef.current = null;
      return corpus;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  // The interest step is specialty-scoped JuisciTopics (closed vocabulary). The
  // server scopes by the user's specialties, so `pmids` is irrelevant. An empty
  // list (specialty not yet covered) is a valid state — the step is skippable.
  async function loadInterests(): Promise<void> {
    setPhase("interests");
    setTopicsLoading(true);
    try {
      await accountReady;
      const response = await apiClient.get("/webapp/private/topics", {
        query: specialtyTagId === null ? {} : { specialtyId: specialtyTagId },
      });
      setTopics(response.body);
    } catch {
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }

  function handleConfirm(): void {
    if (searchResult === null) {
      void loadInterests();
      return;
    }

    // Advance instantly to the specialty-scoped topics step. Persisting the full
    // confirmed publication set (and, for "all", fetching its remaining pages) is
    // decoupled into a background task so the user never waits on it.
    void loadInterests();

    void finalizeConfirmedPublications(searchResult, selection);
  }

  // Background-only: resolve the full selected corpus (fetching the rest in
  // "all" mode), record analytics on the true counts, and POST the confirmed
  // publications. Never blocks the onboarding flow; failures are non-fatal.
  async function finalizeConfirmedPublications(
    result: SearchResult,
    sel: Selection
  ): Promise<void> {
    const corpus =
      sel.mode === "all" ? await ensureAllWorksLoaded() : displayedPublications;
    const selected =
      sel.mode === "all"
        ? corpus.filter((p) => !sel.except.has(pubKey(p)))
        : corpus.filter((p) => sel.keys.has(pubKey(p)));

    gaEventMatchingPublicationsSubmitted({
      total_found_publications: corpus.length,
      total_confirmed_publications: selected.length,
    });
    mixpanelSetUserProperties({
      total_confirmed_publications: selected.length,
    });

    const best = result.candidates.at(0);
    try {
      await apiClient.post("/webapp/private/users/me/author-search/confirm", {
        json: {
          openalexId: best === undefined ? null : best.openalexId,
          orcid: best === undefined ? null : best.orcid,
          publicationName: null,
          publications: selected.map((p) => ({
            doi: p.doi,
            pmid: p.pmid,
            title: p.title,
            journal: p.journal,
            year: yearFromIsoDate(p.publicationDate),
          })),
        },
      });
    } catch {
      // Non-fatal — the user has already moved on.
    }
  }

  function toggleTopic(uid: string): void {
    setSelectedTopicUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }

  async function handleSaveTopics(): Promise<void> {
    setTopicsSaving(true);

    const selectedTopics = topics.filter((topic) =>
      selectedTopicUids.has(topic.uid)
    );
    const confirmedTopics = selectedTopics.map((topic) =>
      getItemTranslation(topic)
    );
    gaEventRelevantTopicsSubmitted({
      flow: "signup",
      total_found_topics: topics.length,
      total_confirmed_topics: selectedTopics.length,
      confirmed_topics: confirmedTopics,
      // Signup picks from the specialty seed list: the account has no
      // conversation history yet, so there is no learned layer to suggest from.
      total_suggested_topics: 0,
      total_suggested_confirmed: 0,
    });
    mixpanelSetUserProperties({ confirmed_topics: confirmedTopics });

    try {
      await apiClient.patch("/webapp/private/users/me/topics", {
        json: { topicUids: selectedTopics.map((topic) => topic.uid) },
      });
    } catch {
      // Non-fatal
    }

    // Moving forward — the unmount that follows must NOT abort the background
    // finalize still persisting the confirmed publications.
    completingRef.current = true;
    onComplete();
  }

  return (
    <>
      {phase === "pubmed-intro" && (
        <PubMedIntroView
          firstName={firstName}
          fullName={fullName}
          onContinue={handleStartSearch}
        />
      )}
      {phase === "searching" && (
        <SearchingView name={fullName} steps={searchSteps} />
      )}
      {phase === "results" && (
        <ResultsView
          publications={displayedPublications}
          selection={selection}
          selectedCount={selectedCount}
          allSelected={allSelected}
          onToggle={togglePublication}
          onToggleAll={toggleAllPublications}
          onConfirm={handleConfirm}
          onReject={() => {
            void loadInterests();
          }}
          totalFound={totalFound}
          onLoadMore={() => {
            void loadMoreWorks();
          }}
          hasMore={worksOffset !== null}
          isFetchingMore={loadingMore}
        />
      )}
      {phase === "interests" && (
        <TopicsView
          topics={topics}
          selectedTopicUids={selectedTopicUids}
          onToggle={toggleTopic}
          onSave={() => {
            void handleSaveTopics();
          }}
          loading={topicsLoading}
          saving={topicsSaving}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PubMed Intro View
// ---------------------------------------------------------------------------

function PubMedIntroView({
  firstName,
  fullName,
  onContinue,
}: {
  firstName: string;
  fullName: string;
  onContinue: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  const bullets = [
    {
      title: t("We look for recent articles with your name as author"),
      description: t("Focused on your current expertise"),
    },
    {
      title: t("You confirm the articles you're author of"),
      description: t("It helps in case of homonyms"),
    },
    {
      title: t("We personalize your feed for you"),
      description: t("No more struggle with MeSH terms"),
    },
  ];

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex flex-col gap-6 items-start">
        <div className="flex flex-col gap-3 w-full">
          <h1 className="font-['DM_Sans',sans-serif] font-semibold text-[30px]/8  tracking-[-1px] text-[#2c2c2c]">
            {t("pubmedIntroTitle", { firstName })}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[15px]/6  tracking-[-0.15px] text-[#929292]">
            {t(
              "We will look for your publications on PubMed so we can better understand your domain of expertise."
            )}
          </p>
        </div>

        <div className="flex w-full justify-center">
          <div className="bg-ds-mint-50 rounded-full h-9 px-8 flex items-center justify-center">
            <span className="font-['DM_Sans',sans-serif] font-semibold text-xs text-black">
              {fullName}
            </span>
          </div>
        </div>

        <div className="w-full rounded-2xl border border-[rgba(11,17,16,0.08)] bg-ds-slate-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="flex size-9.5 shrink-0 items-center justify-center rounded-xl bg-white overflow-hidden">
              <PubMedLogo aria-hidden />
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="font-['DM_Sans',sans-serif] font-semibold text-[13px]/5  tracking-[-0.13px] text-[#212121]">
                {t("PubMed - National Library of Medicine")}
              </p>
              <p className="font-['DM_Sans',sans-serif] text-[11px]/4  tracking-[-0.11px] text-[#929292]">
                {t("Public database - 36M+ indexed articles")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full">
          {bullets.map((b, i) => (
            <div
              key={b.title}
              className={cn(
                "flex flex-col pt-2 pb-3",
                i < bullets.length - 1 && "border-b border-[#e9e9e9]"
              )}
            >
              <p className="font-['DM_Sans',sans-serif] font-semibold text-[13px]/5  tracking-[-0.13px] text-black">
                ・{b.title}
              </p>
              <p className="font-['DM_Sans',sans-serif] text-[11px]/4  tracking-[-0.11px] text-[#929292]">
                {b.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1" />

      <div className="flex flex-col gap-3 shrink-0 pb-2">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={onContinue}
        >
          {t("Continue")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Searching View
// ---------------------------------------------------------------------------

function SearchingView({
  name,
  steps,
}: {
  name: string;
  steps: Array<{ label: string; done: boolean }>;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-between min-h-0">
      <div className="flex flex-1 flex-col gap-6 items-center justify-center w-full">
        <div className="flex flex-col gap-3 text-center w-full">
          <h1 className="font-['DM_Sans',sans-serif] font-bold text-[30px]/8  tracking-[-1px] text-[#2c2c2c]">
            {t("Searching publications...")}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[15px]/6  tracking-[-0.15px] text-[#929292]">
            {t("Looking for publications for")}
          </p>
        </div>

        <div className="bg-[#f3f3f3] rounded-full px-3 py-2">
          <span className="font-['DM_Sans',sans-serif] font-semibold text-xs text-black">
            {name}
          </span>
        </div>

        <div className="flex flex-1 flex-col gap-8 items-center justify-center">
          {steps.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div
                className={cn(
                  "size-6 rounded-full flex items-center justify-center",
                  s.done ? "bg-[#212121]" : "bg-[#ececec]"
                )}
              >
                {s.done ? (
                  <IconCheck size={14} className="text-white" />
                ) : (
                  <IconLoader2
                    size={14}
                    className="text-[#929292] animate-spin"
                  />
                )}
              </div>
              <span
                className={cn(
                  "font-['DM_Sans',sans-serif] text-[13px] w-[150px]",
                  s.done ? "font-semibold text-black" : "text-[#929292]"
                )}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="font-['DM_Sans',sans-serif] text-center text-[11px]/4  tracking-[-0.11px] text-[#929292]">
        {t("Usually under 3 seconds")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results View
// ---------------------------------------------------------------------------

function ResultsView({
  publications,
  selection,
  selectedCount,
  allSelected,
  onToggle,
  onToggleAll,
  onConfirm,
  onReject,
  totalFound,
  onLoadMore,
  hasMore,
  isFetchingMore,
}: {
  publications: Array<Publication & { candidateId: string | null }>;
  selection: Selection;
  selectedCount: number;
  allSelected: boolean;
  onToggle: (pub: Publication) => void;
  onToggleAll: () => void;
  onConfirm: () => void;
  onReject: () => void;
  totalFound: number;
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col gap-3 min-h-0">
      <div className="flex flex-col gap-3 shrink-0">
        <h1 className="font-['DM_Sans',sans-serif] font-bold text-[30px]/8  tracking-[-1px] text-[#2c2c2c]">
          {t("Your publications")}
        </h1>
        <p className="font-['DM_Sans',sans-serif] text-[13px]/5  tracking-[-0.13px] text-[#929292]">
          <Trans
            i18nKey="{{count}} publications found.<br/>Select the ones that are yours."
            t={t}
            values={{ count: totalFound }}
            components={{ br: <br /> }}
          />
        </p>
      </div>

      <div className="flex justify-end shrink-0">
        {allSelected ? (
          <button
            key="unselect-all"
            type="button"
            onClick={onToggleAll}
            translate="no"
            className="font-dm-sans font-medium text-[13px]/5  text-ds-mint-700 underline bg-transparent border-none cursor-pointer p-0"
          >
            {t("Unselect all")}
          </button>
        ) : (
          <button
            key="select-all"
            type="button"
            onClick={onToggleAll}
            translate="no"
            className="font-dm-sans font-medium text-[13px]/5  text-ds-mint-700 underline bg-transparent border-none cursor-pointer p-0"
          >
            {t("Select all")}
          </button>
        )}
      </div>

      <FadeScroll
        className="flex flex-col gap-3"
        onReachBottom={hasMore ? onLoadMore : null}
      >
        {publications.map((pub) => {
          const key = pubKey(pub);
          const authorDisplay =
            pub.authors.length > 0
              ? pub.authors[0] + (pub.authors.length > 1 ? " et al." : "")
              : "";

          return (
            <ListItem
              key={`${key}-${pub.candidateId === null ? "pubmed" : pub.candidateId}`}
              affordance={{ kind: "action" }}
              disabled={false}
              selected={isPubSelected(selection, key)}
              onClick={() => {
                onToggle(pub);
              }}
            >
              {pub.journal !== null && (
                <p className="text-xs leading-[1.3] tracking-[-0.12px] line-clamp-1">
                  <span className="text-ds-slate-700">In </span>
                  <span className="font-medium text-[#161616]">
                    {pub.journal}
                  </span>
                </p>
              )}
              <p className="font-semibold text-[15px]/5  tracking-[-0.3px] text-ds-black">
                {pub.title}
              </p>
              <div className="flex items-center justify-between text-[11px] leading-[1.3] tracking-[-0.11px] text-ds-slate-700 opacity-55">
                {authorDisplay.length > 0 && (
                  <span className="font-medium text-[#010101]">
                    {authorDisplay}
                  </span>
                )}
                {pub.publicationDate === null ? null : (
                  <span>{formatArticleDate(pub.publicationDate, "en")}</span>
                )}
              </div>
            </ListItem>
          );
        })}

        {isFetchingMore && (
          <div className="flex justify-center py-2">
            <IconLoader2 size={20} className="animate-spin text-[#929292]" />
          </div>
        )}
      </FadeScroll>

      <div className="flex flex-col gap-3 shrink-0 pb-2">
        <Button
          size="lg"
          variant="outline"
          className="w-full"
          onClick={onReject}
        >
          {t("None of these articles are mine")}
        </Button>
        <Button
          variant={selectedCount > 0 ? "primary" : undefined}
          size="lg"
          className="w-full"
          disabled={selectedCount === 0}
          onClick={onConfirm}
        >
          {t("Confirm {{count}} selected publications", {
            count: selectedCount,
          })}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Topics View
// ---------------------------------------------------------------------------

function TopicsView({
  topics,
  selectedTopicUids,
  onToggle,
  onSave,
  loading,
  saving,
}: {
  topics: Topic[];
  selectedTopicUids: Set<string>;
  onToggle: (uid: string) => void;
  onSave: () => void;
  loading: boolean;
  saving: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selectedCount = selectedTopicUids.size;
  const isEmpty = !loading && topics.length === 0;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFadeTop, setShowFadeTop] = useState(false);
  const [showFadeBottom, setShowFadeBottom] = useState(true);

  useEffect(() => {
    const container = scrollRef.current;
    if (container === null) return;

    function updateFades(): void {
      if (container === null) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowFadeTop(scrollTop > 4);
      setShowFadeBottom(scrollTop + clientHeight < scrollHeight - 4);
    }

    updateFades();
    container.addEventListener("scroll", updateFades, { passive: true });
    return () => {
      container.removeEventListener("scroll", updateFades);
    };
  }, [loading]);

  return (
    <div className="flex flex-1 flex-col min-h-0 gap-4">
      <div className="flex flex-col gap-2 shrink-0">
        <h1 className="font-['DM_Sans',sans-serif] font-semibold text-[30px]/8  tracking-[-1px] text-[#2c2c2c]">
          {t("Pick your interests")}
        </h1>
        <p className="font-['DM_Sans',sans-serif] text-[15px]/6  tracking-tight text-[#929292]">
          {t("Here are topics that match your specialty.")}
          <br />
          {t("Select the areas you want to follow.")}
        </p>
      </div>

      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <IconLoader2 size={24} className="animate-spin text-[#929292]" />
          <p className="font-['DM_Sans',sans-serif] text-[13px] text-[#929292]">
            {t("Loading topics...")}
          </p>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
          <p className="font-['DM_Sans',sans-serif] text-[15px]/6 tracking-tight text-[#929292]">
            {t(
              "No topics for your specialty yet.\nYou can continue. Your feed will adapt to your conversations."
            )}
          </p>
        </div>
      ) : (
        <div className="relative flex-1 min-h-0">
          <div
            className={cn(
              "pointer-events-none absolute inset-x-0  top-0 h-6 z-10",
              "bg-linear-to-b from-[#fbfbfb] to-transparent",
              "transition-opacity duration-200",
              showFadeTop ? "opacity-100" : "opacity-0"
            )}
          />
          <div
            className={cn(
              "pointer-events-none absolute inset-x-0  bottom-0 h-6 z-10",
              "bg-linear-to-t from-[#fbfbfb] to-transparent",
              "transition-opacity duration-200",
              showFadeBottom ? "opacity-100" : "opacity-0"
            )}
          />
          <div
            ref={scrollRef}
            className="flex flex-wrap gap-2 py-4 h-full content-start overflow-y-auto"
          >
            {topics.map((topic) => (
              <ListItem
                key={topic.uid}
                affordance={{ kind: "action" }}
                disabled={false}
                selected={selectedTopicUids.has(topic.uid)}
                onClick={() => {
                  onToggle(topic.uid);
                }}
                className="w-auto"
              >
                {getItemTranslation(topic)}
              </ListItem>
            ))}
          </div>
        </div>
      )}

      <div className="shrink-0 pb-2">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={saving}
          onClick={onSave}
        >
          {saving ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : selectedCount === 0 ? (
            t("Continue")
          ) : (
            t("Save {{count}} topics", { count: selectedCount })
          )}
        </Button>
      </div>
    </div>
  );
}
